import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClipboard } from '../context/ClipboardContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemText, TextField,
  Alert, IconButton, Divider, FormControlLabel, Switch, Backdrop,
  Autocomplete, createFilterOptions, Snackbar, CircularProgress
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import BiotechIcon from '@mui/icons-material/Biotech';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// --- INTERFACES ---
interface Patient {
  id: string;
  name: string;
  lastName: string;
  patientId?: string;
  cedula?: string;
  idNumber?: string;
  birthDate?: string;
}

interface Exam {
  id: string;
  name: string;
  description: string;
  unit?: string;
}

interface RequestSide {
  exams: Exam[];
  note: string;
  showData: boolean;
}

interface Config {
  logoUrl?: string;
  headerImageUrl?: string;
  logoRecipeUrl?: string;
  footerAddress?: string;
}

interface LabRequest {
  id: string;
  patientId: string;
  medications: Exam[];
  note?: string;
  createdAt: string;
  type?: string;
}

//const filter = createFilterOptions<Patient>();

function SolicitudParaclinicosPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { clipboardItems, clearClipboard } = useClipboard();

  // --- ESTADOS ---
  const [allPatients, setAllPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isPatientSelectionRequired, setIsPatientSelectionRequired] = useState(false);
  const [currentSignature, setCurrentSignature] = useState('');
  
  // --- PRE-CARGA DE IMÁGENES ---
  const [logoBase64, setLogoBase64] = useState('');
  const [sigBase64, setSigBase64] = useState('');

  // Estado para catálogo completo
  const [allExams, setAllExams] = useState<Exam[]>([]);
  
  const [activeSide, setActiveSide] = useState<'left' | 'right'>('left');
  const [leftSide, setLeftSide] = useState<RequestSide>({ exams: [], note: '', showData: true });
  const [rightSide, setRightSide] = useState<RequestSide>({ exams: [], note: '', showData: true });
  
  const [config, setConfig] = useState<Config>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pastRequests, setPastRequests] = useState<LabRequest[]>([]);
  
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSavingCloud, setIsSavingCloud] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [processStatus, setProcessStatus] = useState('');

  // --- UTILIDAD: Convertir a Base64 (CORREGIDA PARA SUPABASE) ---
  const fetchAsBase64 = useCallback(async (rawUrl: string) => {
    if (!rawUrl) return '';
    if (rawUrl.startsWith('data:')) return rawUrl;

    try {
      const token = localStorage.getItem('token');
      
      // CORRECCIÓN CRÍTICA: No recortamos el dominio si es http (Supabase)
      let urlToSend = rawUrl;
      if (!rawUrl.startsWith('http')) {
          // Si es ruta relativa (/uploads...), le pegamos la API_URL
          urlToSend = rawUrl.startsWith('/') ? `${API_URL}${rawUrl}` : `${API_URL}/${rawUrl}`;
      }

      // Enviamos la URL completa al proxy
      const proxyUrl = `${API_URL}/api/proxy-image?url=${encodeURIComponent(urlToSend)}`;
      
      const res = await fetch(proxyUrl, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error("Proxy error");
      const data = await res.json();
      return data.base64;
    } catch (err) {
      console.error("Error pre-cargando imagen:", rawUrl, err);
      return '';
    }
  }, []);

// 2. EFECTO PARA SINCRONIZAR FIRMA
  useEffect(() => {
      const fetchUserData = async () => {
          if (user?.id) {
              try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/api/users/${user.id}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (res.ok) {
                      const userData = await res.json();
                      setCurrentSignature(userData.signatureUrl || userData.signature_url || '');
                  }
              } catch (error) {
                  console.error("Error buscando firma:", error);
              }
          }
      };
      fetchUserData();
  }, [user?.id]);

// --- EFECTO: Pre-cargar Logo y Firma ---
  useEffect(() => {
    const loadAssets = async () => {
      // 1. Cargar Logo
      const rawLogo = config.logoRecipeUrl || config.logoUrl || config.headerImageUrl;
      if (rawLogo && !logoBase64) {
        const b64 = await fetchAsBase64(rawLogo);
        setLogoBase64(b64);
      }

      // 2. Cargar Firma
      const rawSig = currentSignature || (user as any)?.signature_url || (user as any)?.signatureUrl;
      
      if (rawSig) { 
        const b64 = await fetchAsBase64(rawSig);
        setSigBase64(b64);
      }
    };

    if (Object.keys(config).length > 0 || user || currentSignature) {
      loadAssets();
    }
  }, [config, user, fetchAsBase64, logoBase64, currentSignature]); 


  // --- CARGA INICIAL ---
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }

      try {
        const headers: HeadersInit = { 'Authorization': `Bearer ${token}` };
        const [catalogoRes, configRes, patientsRes] = await Promise.all([
          fetch(`${API_URL}/api/paraclinicos/catalogo`, { headers }),
          fetch(`${API_URL}/api/config`, { headers }),
          fetch(`${API_URL}/api/patients`, { headers }),
        ]);

        if (mounted) {
          if (configRes.ok) setConfig(await configRes.json());
          if (patientsRes.ok) setAllPatients(await patientsRes.json());
          
          if (catalogoRes.ok) {
            const rawData = await catalogoRes.json();
            let flatList: Exam[] = [];
            if (Array.isArray(rawData)) {
                rawData.forEach((grupo: any) => {
                    if (grupo.options && Array.isArray(grupo.options)) {
                        grupo.options.forEach((opt: any) => {
                            flatList.push({
                                id: opt.value || opt.id,
                                name: opt.label || opt.nombre || 'Sin Nombre',
                                description: grupo.label || opt.categoria || 'General',
                                unit: opt.unit || opt.unidad || ''
                            });
                        });
                    }
                });
            }
            setAllExams(flatList);
          }

          if (!selectedPatient) setIsPatientSelectionRequired(true);
        }
      } catch (error) {
        if (mounted) setError("Error al conectar con el servidor.");
      } finally {
        if (mounted) setIsInitialLoading(false);
      }
    };
    init();
    return () => { mounted = false; };
  }, [navigate, selectedPatient]);

  // --- HISTORIAL ---
  const fetchPastRequests = useCallback(async (patientId: string) => {
    if (!patientId) return setPastRequests([]);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/recipes?patientId=${patientId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data: any[] = await res.json();
        setPastRequests(
          data.filter((r: any) => r.type === 'lab_request')
              .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        );
      }
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => { if (selectedPatient) fetchPastRequests(selectedPatient.id); }, [selectedPatient, fetchPastRequests]);

  // --- HANDLERS UI ---
  const handleAddExam = (exam: Exam) => {
    const currentSide = activeSide === 'left' ? leftSide : rightSide;
    if (currentSide.exams.find(e => e.id === exam.id)) {
        setError("El examen ya está en la lista.");
        return;
    }
    const examToAdd = { ...exam };
    if (activeSide === 'left') setLeftSide(prev => ({ ...prev, exams: [...prev.exams, examToAdd] }));
    else setRightSide(prev => ({ ...prev, exams: [...prev.exams, examToAdd] }));
  };

  const handleRemoveExam = (examId: string) => {
    if (activeSide === 'left') setLeftSide(prev => ({ ...prev, exams: prev.exams.filter(s => s.id !== examId) }));
    else setRightSide(prev => ({ ...prev, exams: prev.exams.filter(s => s.id !== examId) }));
  };

  const handleNoteChange = (text: string) => {
    if (activeSide === 'left') setLeftSide(prev => ({ ...prev, note: text }));
    else setRightSide(prev => ({ ...prev, note: text }));
  };

  const handleSaveRequest = async () => {
    const allExamsToSave = [...leftSide.exams, ...rightSide.exams];
    if (!selectedPatient || allExamsToSave.length === 0) { setError('No hay exámenes.'); return; }
    
    try {
      await fetch(`${API_URL}/api/recipes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          patientId: selectedPatient.id, 
          medications: allExamsToSave, 
          type: 'lab_request',
          note: `[Izq]: ${leftSide.note} | [Der]: ${rightSide.note}`, 
          createdBy: user?.id, 
          doctorName: user?.name,
          doctorId: user?.id // <-- CORRECCIÓN: Agregamos el ID para vincular al historial
        }),
      });
      await fetchPastRequests(selectedPatient.id);
      setSuccess('Solicitud guardada.');
    } catch (error) { setError('Error al guardar.'); }
  };

  // --- GENERACIÓN PDF ---
  const generatePDFBlob = async () => {
    const printElement = document.getElementById('internal-print-view');
    if (!printElement) return null;

    const canvas = await html2canvas(printElement, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: "#ffffff",
        logging: false 
    });
    const imgData = canvas.toDataURL('image/jpeg', 0.98);

    const pdf = new jsPDF('l', 'mm', 'letter');
    pdf.addImage(imgData, 'JPEG', 0, 0, 279.4, 215.9);
    return pdf;
  };

  const handlePrint = async () => {
    if (leftSide.exams.length === 0 && rightSide.exams.length === 0) { 
        setError("Nada que imprimir."); 
        return; 
    }
    
    setIsPrinting(true);
    setProcessStatus("Generando vista previa...");
    
    // Aumentamos tiempo de espera para asegurar carga de imágenes
    setTimeout(async () => {
        try {
            const pdf = await generatePDFBlob();
            if (pdf) {
                const blobUrl = URL.createObjectURL(pdf.output('blob'));
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = blobUrl;
                document.body.appendChild(iframe);
                iframe.onload = () => {
                    setTimeout(() => {
                        iframe.contentWindow?.print();
                        setIsPrinting(false);
                        setProcessStatus("");
                    }, 500);
                };
            } else { setIsPrinting(false); }
        } catch (e) {
            console.error(e);
            setIsPrinting(false);
            setError("Error al generar impresión.");
        }
    }, 2000); 
  };

  const handleArchiveToExpediente = async () => {
    if (!selectedPatient) { setError("Faltan datos."); return; }
    setIsPrinting(true); 
    setIsSavingCloud(true);
    setProcessStatus("Archivando...");

    setTimeout(async () => {
        try {
            const pdf = await generatePDFBlob();
            if (pdf) {
                const formData = new FormData();
                formData.append('file', pdf.output('blob'), `Laboratorio_${selectedPatient.lastName}.pdf`);
                formData.append('patientId', selectedPatient.id);
                formData.append('title', `Orden de Laboratorio - ${new Date().toLocaleDateString()}`);
                formData.append('category', 'LABORATORIO');

                const res = await fetch(`${API_URL}/api/documents`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    body: formData
                });

                if (res.ok) setSuccess("✅ Archivado en Expediente");
                else throw new Error("Error subiendo");
            }
        } catch (e) { setError("Error al archivar."); } 
        finally {
            setIsSavingCloud(false);
            setIsPrinting(false);
            setProcessStatus("");
        }
    }, 2000); // Aumentado para seguridad
  };

  const filteredExams = allExams.filter(exam => 
    exam.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    exam.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isInitialLoading) return <Box sx={{display:'flex', justifyContent:'center', mt:10}}><CircularProgress /></Box>;

  if (!selectedPatient || isPatientSelectionRequired) {
    return (
      <Dialog open={true} maxWidth="sm" fullWidth>
        <DialogTitle>Selección de Paciente</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            options={allPatients}
            getOptionLabel={(p) => `${p.name} ${p.lastName} (${p.cedula || 'S/C'})`}
            onChange={(_, v) => { if(v) { setSelectedPatient(v); setIsPatientSelectionRequired(false); } }}
            renderInput={(p) => <TextField {...p} label="Buscar Paciente" autoFocus />}
          />
        </DialogContent>
        <DialogActions><Button onClick={() => navigate('/dashboard')}>Salir</Button></DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column', gap: 2 }} open={isPrinting || isSavingCloud}>
        <CircularProgress color="inherit" />
        <Typography>{processStatus}</Typography>
      </Backdrop>

      <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: 'white', borderBottom: '1px solid #ccc', p: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" color="error" startIcon={<ExitToAppIcon />} onClick={() => navigate('/dashboard')}>SALIR</Button>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', mx: 2 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                {selectedPatient.name} {selectedPatient.lastName}
            </Typography>
            <Typography variant="body1" sx={{ color: '#333', fontSize: '0.90rem', fontWeight: 'bold' }}>
                Cedula N° {selectedPatient.cedula || selectedPatient.patientId || 'S/C'}
            </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Button variant="outlined" color="warning" onClick={() => { if(window.confirm('¿Reiniciar?')) { setLeftSide({exams:[],note:'',showData:true}); setRightSide({exams:[],note:'',showData:true}); } }}>Reiniciar</Button>

        <FormControlLabel control={<Switch checked={includeSignature} onChange={(e) => setIncludeSignature(e.target.checked)} size="small" />} label={<Typography variant="caption" fontWeight="bold">FIRMA</Typography>} sx={{ ml: 1 }} />

        <Button variant="contained" color="info" onClick={handleArchiveToExpediente} startIcon={isSavingCloud ? <CircularProgress size={20} color="inherit"/> : <CloudUploadIcon />} disabled={isSavingCloud}>
            ARCHIVAR EN EXPEDIENTE
        </Button>

        <Button variant="contained" onClick={handlePrint} startIcon={<PrintIcon />}>IMPRIMIR</Button>
        
        <Button variant="contained" color="success" onClick={handleSaveRequest} startIcon={<SaveIcon />}>GUARDAR</Button>

        <Button variant="outlined" startIcon={<PersonSearchIcon />} onClick={() => setIsPatientSelectionRequired(true)} sx={{ ml: 1 }}>
            Cambiar
        </Button>
      </Box>

      <Box sx={{ p: 3, display: 'flex', gap: 3, height: 'calc(100vh - 85px)' }}>
        <Paper sx={{ width: 350, display: 'flex', flexDirection: 'column', p: 2 }}>
            <Typography variant="subtitle1" color="primary" sx={{mb:1, display:'flex', alignItems:'center', gap:1}}><BiotechIcon fontSize="small"/> Catálogo</Typography>
            <TextField 
                fullWidth placeholder="Buscar examen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon color="action" /> }} sx={{ mb: 2 }} size="small"
            />
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <List dense>
                    {filteredExams.map((exam, i) => (
                        <ListItem key={`${exam.id}-${i}`} button divider onClick={() => handleAddExam(exam)}>
                            <ListItemText primary={exam.name} secondary={exam.description} />
                            <AddIcon color="primary" />
                        </ListItem>
                    ))}
                    {filteredExams.length === 0 && <Typography variant="caption" sx={{p:2}}>No encontrado</Typography>}
                </List>
            </Box>
        </Paper>

        <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
            <Paper sx={{ flex: 1, p: 2, border: activeSide==='left'?'2px solid #1976d2':'1px solid #ddd', display:'flex', flexDirection:'column' }} onClick={()=>setActiveSide('left')}>
                <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <Typography variant="subtitle1" color="primary" fontWeight="bold">ORDEN 1 (IZQ)</Typography>
                    <Button 
                        size="small" variant="contained" color={leftSide.showData ? "success" : "error"}
                        startIcon={leftSide.showData ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        onClick={(e) => { e.stopPropagation(); setLeftSide(prev => ({...prev, showData: !prev.showData})); }}
                    >
                        {leftSide.showData ? "DATOS VISIBLES" : "DATOS BORRADOS"}
                    </Button>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ flexGrow: 1, overflowY: 'auto', opacity: leftSide.showData ? 1 : 0.3 }}>
                    <List dense>
                        {leftSide.exams.map((s, i) => (
                            <ListItem key={i} secondaryAction={<IconButton size="small" onClick={()=>handleRemoveExam(s.id)}><DeleteIcon color="error" fontSize="small"/></IconButton>}>
                                <Typography variant="caption" sx={{mr:1, fontWeight:'bold'}}>{i+1}.</Typography>
                                <ListItemText primary={s.name} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
                <TextField fullWidth multiline rows={2} placeholder="Nota..." value={leftSide.note} onChange={e=>handleNoteChange(e.target.value)} size="small" sx={{mt:1}}/>
            </Paper>

            <Paper sx={{ flex: 1, p: 2, border: activeSide==='right'?'2px solid #9c27b0':'1px solid #ddd', display:'flex', flexDirection:'column' }} onClick={()=>setActiveSide('right')}>
                <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <Typography variant="subtitle1" color="secondary" fontWeight="bold">ORDEN 2 (DER)</Typography>
                    <Button 
                        size="small" variant="contained" color={rightSide.showData ? "success" : "error"}
                        startIcon={rightSide.showData ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        onClick={(e) => { e.stopPropagation(); setRightSide(prev => ({...prev, showData: !prev.showData})); }}
                    >
                        {rightSide.showData ? "DATOS VISIBLES" : "DATOS BORRADOS"}
                    </Button>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ flexGrow: 1, overflowY: 'auto', opacity: rightSide.showData ? 1 : 0.3 }}>
                    <List dense>
                        {rightSide.exams.map((s, i) => (
                            <ListItem key={i} secondaryAction={<IconButton size="small" onClick={()=>handleRemoveExam(s.id)}><DeleteIcon color="error" fontSize="small"/></IconButton>}>
                                <Typography variant="caption" sx={{mr:1, fontWeight:'bold'}}>{i+1}.</Typography>
                                <ListItemText primary={s.name} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
                <TextField fullWidth multiline rows={2} placeholder="Nota..." value={rightSide.note} onChange={e=>handleNoteChange(e.target.value)} size="small" sx={{mt:1}}/>
            </Paper>
        </Box>
      </Box>

      {isPrinting && (
        <Box sx={{ position: 'fixed', top: -9999, left: -9999, zIndex: 9999 }}>
            <InternalPrintView 
                patient={selectedPatient} 
                left={leftSide} 
                right={rightSide} 
                config={config} 
                includeSignature={includeSignature}
                logoBase64={logoBase64}
                sigBase64={sigBase64}
            />
        </Box>
      )}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={()=>setSuccess('')}><Alert severity="success">{success}</Alert></Snackbar>
      <Snackbar open={!!error} autoHideDuration={3000} onClose={()=>setError('')}><Alert severity="error">{error}</Alert></Snackbar>
    </>
  );
}

// --- COMPONENTE INTERNO DE IMPRESIÓN ---
const InternalPrintView = ({ patient, left, right, config, includeSignature, logoBase64, sigBase64 }: any) => {
    
    const calculateAge = (birthDate?: string) => {
        if (!birthDate) return '---';
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
        return `${age} años`;
    };

    const Column = ({ data }: any) => (
        <td style={{ width: 'calc(50% - 7mm)', verticalAlign: 'top', padding: '0 0 0 5px', height: '100%' }}>
            <div style={{ height: '202mm', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                        {/* CAMBIO: Logo SIEMPRE visible, independiente de showData */}
                        {logoBase64 ? <img src={logoBase64} style={{ maxHeight: '65px' }} alt="Logo" /> : <div style={{height:65}}/>}
                    </div>
                    
                    {/* CAMBIO: Borde SIEMPRE visible, el contenido depende de showData */}
                    <div style={{ border: '1px solid #000', padding: '4px', marginBottom: '15px', fontSize: '8pt', fontFamily: 'Arial', minHeight: '35px' }}>
                        {data.showData ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}><b>NOMBRE:</b> <span style={{ fontSize: '9pt', fontWeight: 'bold' }}>{patient.name.toUpperCase()} {patient.lastName.toUpperCase()}</span></td>
                                        <td style={{ textAlign: 'right', width: '100px' }}><b>EDAD:</b> {calculateAge(patient.birthDate)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left', fontSize: '9pt' }}><b>C.I.:</b> {patient.cedula || patient.patientId || '---'}</td>
                                        <td style={{ textAlign: 'right' }}><b>FECHA:</b> {new Date().toLocaleDateString()}</td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ height: '30px' }} />
                        )}
                    </div>

                    <div style={{ minHeight: '400px', fontSize: '10pt', lineHeight: '1.3', fontFamily: 'Arial' }}>
                        <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: '10px', textAlign: 'center', display: data.showData ? 'block' : 'none' }}>SOLICITUD DE EXÁMENES</div>
                        {data.showData && data.exams.map((s: any, i: number) => (
                            <div key={i} style={{ marginBottom: '8px' }}>
                                <b>{i + 1}. {s.name}</b>
                                {s.description && s.description !== 'General' && <div style={{ fontSize: '9pt', color: '#333' }}>{s.description}</div>}
                            </div>
                        ))}
                        {data.showData && data.note && <div style={{ marginTop: '15px', fontStyle: 'italic' }}>Nota: {data.note}</div>}
                    </div>
                </div>

                <div>
                    <div style={{ minHeight: '80px', marginBottom: '5px' }}>
                        {/* Firma SÍ depende de showData */}
                        {data.showData && includeSignature && sigBase64 ? (
                            <div style={{ textAlign: 'center' }}>
                                <img src={sigBase64} style={{ maxHeight: '80px', display: 'block', margin: '0 auto' }} alt="Firma" />
                            </div>
                        ) : (
                            data.showData && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <div style={{ height: '40px', borderBottom: '1px solid #000', width: '140px', margin: '0 auto' }}></div>
                                    <small style={{ fontSize: '7pt' }}>SELLO Y FIRMA</small>
                                </div>
                            )
                        )}
                    </div>

                    {/* Footer SIEMPRE visible */}
                    {config.footerAddress && (
                        <div style={{ borderTop: '0.3px solid #ccc', paddingTop: '3px', textAlign: 'center', fontSize: '7pt', color: '#666', fontFamily: 'Arial' }}>
                            {config.footerAddress}
                        </div>
                    )}
                </div>
            </div>
        </td>
    );

    return (
        <div id="internal-print-view" style={{ width: '279.4mm', height: '215.9mm', backgroundColor: 'white', padding: '6mm', boxSizing: 'border-box' }}>
            <table style={{ width: '100%', height: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <tbody>
                    <tr>
                        <Column data={left} />
                        <td style={{ width: '14mm' }}>&nbsp;</td>
                        <Column data={right} />
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default SolicitudParaclinicosPage;